/**
 * PlayPoList is stored as comma-separated values. Two shapes exist:
 * - Partner/plan pairs: [partnerCodes…, planCodes…] (even length, validated via catalog)
 * - Plain mix services: product offering IDs from device + subscription flow (e.g. PlayPOMEDIA…)
 */

function normalizeCode (code) {
  return typeof code === 'string' ? code.trim() : ''
}

export function buildPartnerCodeSet (catalog) {
  const partnerCodes = new Set()
  if (!Array.isArray(catalog)) {
    return partnerCodes
  }
  catalog.forEach((partner) => {
    const code = normalizeCode(partner?.code)
    const product = normalizeCode(partner?.product)
    if (code) {
      partnerCodes.add(code)
    }
    if (product) {
      partnerCodes.add(product)
    }
  })
  return partnerCodes
}

export function parsePlayPoListParts (playPoListValue) {
  if (typeof playPoListValue !== 'string') {
    return []
  }
  return playPoListValue.split(',').map(normalizeCode).filter(Boolean)
}

function isPartnerPlanSplit (parts, half, planMap, partnerCodes) {
  if (!planMap?.size || !partnerCodes?.size) {
    return false
  }
  const firstHalf = parts.slice(0, half)
  const secondHalf = parts.slice(half)
  const allSecondArePlans = secondHalf.every(code => planMap.has(code))
  const allFirstArePartners = firstHalf.every(code => partnerCodes.has(code))
  const firstHalfHasNoPlans = firstHalf.every(code => !planMap.has(code))
  return allSecondArePlans && allFirstArePartners && firstHalfHasNoPlans
}

function asPartnerPlanResult (parts, half) {
  const partners = parts.slice(0, half)
  const plans = parts.slice(half)
  return { partners, plans, iconCodes: partners }
}

function asPlainMixResult (parts) {
  return { partners: parts, plans: [], iconCodes: parts }
}

function appendUniqueIconCode (codes, seenUrls, code, url) {
  if (url && seenUrls.has(url)) {
    return
  }
  if (url) {
    seenUrls.add(url)
  }
  codes.push(code)
}

/**
 * Resolve icon codes from all PlayPoList parts, deduping by resolved image URL.
 * Keeps codes without a URL so icons can appear once promotional content loads.
 */
export function resolvePlayMixIconCodes (parts, resolveImageUrl) {
  if (!parts.length) {
    return null
  }
  const seenUrls = new Set()
  const codes = []
  for (const code of parts) {
    appendUniqueIconCode(codes, seenUrls, code, resolveImageUrl(code))
  }
  return codes.length ? codes : null
}

/**
 * @returns {{ partners: string[]|null, plans: string[], iconCodes: string[] }}
 */
export function parsePlayPoList (playPoListValue, { planMap, partnerCodes } = {}) {
  const parts = parsePlayPoListParts(playPoListValue)
  if (!parts.length) {
    return { partners: null, plans: [], iconCodes: [] }
  }

  const half = parts.length / 2
  if (parts.length % 2 === 0 && half >= 1 && isPartnerPlanSplit(parts, half, planMap, partnerCodes)) {
    return asPartnerPlanResult(parts, half)
  }

  return asPlainMixResult(parts)
}
